import {
  anchorProfileKindSchema,
  entityViewFields,
  type EntityViewFields,
} from "@brains/plugins";
import { z } from "@brains/utils/zod";
import { renderedLinkFields, viewLinkFields } from "@brains/templates";
import { agentSkillSchema, agentStatusSchema } from "../schemas/agent";

const nullableString: z.ZodDefault<z.ZodNullable<z.ZodString>> = z
  .string()
  .nullable()
  .default(null);
const frontmatterSchema: z.ZodObject<{
  name: z.ZodString;
  kind: typeof anchorProfileKindSchema;
  organization: typeof nullableString;
  brainName: z.ZodString;
  url: z.ZodURL;
  repoDid: typeof nullableString;
  brainDid: typeof nullableString;
  cardUri: typeof nullableString;
  cardCid: typeof nullableString;
  introducedBy: z.ZodDefault<z.ZodNullable<z.ZodArray<z.ZodString>>>;
  hops: z.ZodDefault<z.ZodNullable<z.ZodNumber>>;
  status: typeof agentStatusSchema;
  discoveredAt: z.ZodString;
}> = z.object({
  name: z.string(),
  kind: anchorProfileKindSchema,
  organization: nullableString,
  brainName: z.string(),
  url: z.url(),
  repoDid: nullableString,
  brainDid: nullableString,
  cardUri: nullableString,
  cardCid: nullableString,
  introducedBy: z.array(z.string()).nullable().default(null),
  hops: z.number().nullable().default(null),
  status: agentStatusSchema,
  discoveredAt: z.string(),
});
const metadataSchema: z.ZodObject<{
  name: z.ZodString;
  url: z.ZodURL;
  status: typeof agentStatusSchema;
  discoveredAt: typeof nullableString;
  slug: z.ZodString;
  repoDid: typeof nullableString;
  brainDid: typeof nullableString;
  cardUri: typeof nullableString;
  cardCid: typeof nullableString;
}> = z.object({
  name: z.string(),
  url: z.url(),
  status: agentStatusSchema,
  discoveredAt: nullableString,
  slug: z.string(),
  repoDid: nullableString,
  brainDid: nullableString,
  cardUri: nullableString,
  cardCid: nullableString,
});

export const agentViewSchema: z.ZodObject<
  EntityViewFields<"agent", typeof metadataSchema, typeof frontmatterSchema> & {
    about: z.ZodString;
    skills: z.ZodArray<typeof agentSkillSchema>;
    notes: z.ZodString;
    url: typeof viewLinkFields.url;
    typeLabel: typeof viewLinkFields.typeLabel;
  }
> = z.object({
  ...entityViewFields("agent", metadataSchema, frontmatterSchema),
  about: z.string(),
  skills: z.array(agentSkillSchema),
  notes: z.string(),
  url: viewLinkFields.url,
  typeLabel: viewLinkFields.typeLabel,
});

export type AgentSchemaData = z.output<typeof agentViewSchema>;

export type AgentView = Omit<AgentSchemaData, "url" | "typeLabel"> & {
  url: string;
  typeLabel: string;
};

/** An agent once site-builder has filled the link fields it renders. */
export const agentRenderSchema: z.ZodType<AgentView> = agentViewSchema.extend({
  url: renderedLinkFields.url,
  typeLabel: renderedLinkFields.typeLabel,
});
